import { ThreeEvent } from '@react-three/fiber';
import { use3DRightClick } from 'hooks/use-3d-right-click';
import { DirectPointDragHandlers } from 'hooks/use-direct-point-drag';
import { forwardRef, useState } from 'react';
import { Mesh } from 'three';
import {
  getLinesetHandleHoverColor,
  LINESET_DEFAULT_COLOR,
  LINESET_SELECTED_HANDLE_COLOR
} from 'utils/3d-lineset-color';
import {
  getOpacityMaterialProps,
  getOpacityRenderOrder
} from 'utils/3d-opacity';

type LinesetPoint3DProps = {
  position?: [number, number, number];
  opacity?: number;
  color?: number;
  selected?: boolean;
  onClick?: (event: ThreeEvent<MouseEvent>) => void;
  onContextMenu?: (event: ThreeEvent<PointerEvent>) => void;
  dragHandlers?: DirectPointDragHandlers;
};

const POINT_RADIUS = 0.09;
const POINT_HIT_RADIUS = 0.2;

const LinesetPoint3D = forwardRef<Mesh, LinesetPoint3DProps>(
  (
    {
      position,
      opacity = 1,
      color = LINESET_DEFAULT_COLOR,
      selected = false,
      onClick,
      onContextMenu,
      dragHandlers
    },
    ref
  ) => {
    const [hovered, setHovered] = useState(false);
    const rightClick = use3DRightClick(onContextMenu);

    let pointColor = color;
    if (selected) {
      pointColor = LINESET_SELECTED_HANDLE_COLOR;
    } else if (hovered) {
      pointColor = getLinesetHandleHoverColor(color);
    }

    return (
      <mesh
        ref={ref}
        position={position}
        scale={hovered && !selected ? 1.35 : 1}
        renderOrder={getOpacityRenderOrder(opacity)}
        userData={{ isLinesetPoint: true }}
        onClick={onClick}
        onPointerDown={(event) => {
          rightClick.onPointerDown(event);
          dragHandlers?.onPointerDown(event);
        }}
        onPointerMove={dragHandlers?.onPointerMove}
        onPointerUp={(event) => {
          rightClick.onPointerUp(event);
          dragHandlers?.onPointerUp(event);
        }}
        onPointerCancel={dragHandlers?.onPointerCancel}
        onPointerOver={(event) => {
          event.stopPropagation();
          setHovered(true);
          if (dragHandlers) {
            document.body.style.cursor = 'grab';
          }
        }}
        onPointerOut={() => {
          setHovered(false);
          document.body.style.cursor = '';
        }}
      >
        <sphereGeometry args={[POINT_RADIUS, 16, 16]} />
        <meshStandardMaterial
          color={pointColor}
          {...getOpacityMaterialProps(opacity)}
        />
        <mesh userData={{ isLinesetPoint: true }}>
          <sphereGeometry args={[POINT_HIT_RADIUS, 8, 8]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      </mesh>
    );
  }
);

LinesetPoint3D.displayName = 'LinesetPoint3D';

export default LinesetPoint3D;
